import { useCallback, useRef } from "react";

/**
 * CSS-driven linked hover for the Master-Schedule name grids, with ZERO React
 * render on hover.
 *
 * Hovering a nurse's chip should light every chip of that same nurse and dim the
 * rest. Doing that through `useState` re-rendered the ENTIRE grid (every section,
 * row and cell) twice per chip crossed. Instead this hook mutates the DOM directly:
 *  - it toggles a `data-linked-hover` flag on a STABLE grid ancestor (`gridRef`);
 *    `NameChip`'s CSS dims every non-match chip while that flag is present.
 *  - it marks the hovered nurse's chips (`[data-linked-hover-id="…"]`) with
 *    `data-highlight="match"`, which `NameChip` renders as the brand-tinted ring.
 *
 * No component re-renders — the match/dim look lives entirely in `NameChip`'s
 * attribute-keyed CSS, so this only flips attributes on a handful of nodes.
 */
export type LinkedHoverController = {
	/** Attach to the stable grid ancestor that carries the `data-linked-hover` flag + chips. */
	gridRef: React.RefObject<HTMLDivElement | null>;
	/** Set the hovered nurse (highlights their chips, dims the rest) or clear it with `null`. */
	setHovered: (nurseId: number | null) => void;
};

export const useLinkedHover = (): LinkedHoverController => {
	const gridRef = useRef<HTMLDivElement | null>(null);
	// The chip nodes currently marked as "match", so we can reset exactly them.
	const markedRef = useRef<HTMLElement[]>([]);

	const setHovered = useCallback((nurseId: number | null) => {
		const grid = gridRef.current;
		if (!grid) return;
		// Reset the previously-highlighted group back to neutral.
		for (const node of markedRef.current) node.setAttribute("data-highlight", "none");
		markedRef.current = [];
		if (nurseId === null) {
			delete grid.dataset.linkedHover;
			return;
		}
		grid.dataset.linkedHover = String(nurseId);
		const matches = grid.querySelectorAll<HTMLElement>(`[data-linked-hover-id="${nurseId}"]`);
		for (const node of matches) {
			node.setAttribute("data-highlight", "match");
			markedRef.current.push(node);
		}
	}, []);

	return { gridRef, setHovered };
};
